import type { OrderMessageView } from "@/api/autoparts";

export const ORDER_MESSAGE_READ_STATE_EVENT =
  "sparelynk:order-message-read-state-updated";

export type OrderMessageReaderRole = "seller" | "courier" | "buyer";

function getStorageKey(
  userId: string,
  role: OrderMessageReaderRole,
  orderId: string,
  vendorId?: string,
): string {
  return `sparelynk:order-message-read:${role}:${userId}:${orderId}:${vendorId || "buyer"}`;
}

function parseReadAt(value?: string): number {
  const timestamp = value ? Date.parse(value) : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
}

export function readOrderMessageReadAt(
  userId: string,
  role: OrderMessageReaderRole,
  orderId: string,
  vendorId?: string,
): number {
  if (typeof window === "undefined" || !userId || !orderId) return 0;

  try {
    const value = window.localStorage.getItem(
      getStorageKey(userId, role, orderId, vendorId),
    );
    return parseReadAt(value ?? undefined);
  } catch {
    return 0;
  }
}

export function resolveOrderMessageReadAt(
  userId: string,
  role: OrderMessageReaderRole,
  orderId: string,
  serverReadAt?: string,
  vendorId?: string,
): number {
  return Math.max(
    readOrderMessageReadAt(userId, role, orderId, vendorId),
    parseReadAt(serverReadAt),
  );
}

export function markOrderMessagesRead(
  userId: string,
  role: OrderMessageReaderRole,
  orderId: string,
  readAt = new Date().toISOString(),
  vendorId?: string,
): void {
  if (typeof window === "undefined" || !userId || !orderId) return;

  try {
    window.localStorage.setItem(getStorageKey(userId, role, orderId, vendorId), readAt);
  } catch {
    // Message read state can remain session-only when browser storage is blocked.
  }

  window.dispatchEvent(
    new CustomEvent(ORDER_MESSAGE_READ_STATE_EVENT, {
      detail: { userId, role, orderId, vendorId, readAt },
    }),
  );
}

export function countUnreadOrderMessages(
  messages: OrderMessageView[] | undefined,
  userId: string,
  role: OrderMessageReaderRole,
  orderId: string,
  serverReadAt?: string,
  vendorId?: string,
): number {
  const incomingRole = role === "seller" || role === "buyer" ? "courier" : "seller";
  const readAt = resolveOrderMessageReadAt(userId, role, orderId, serverReadAt, vendorId);

  return (messages ?? []).filter((message) => {
    if (vendorId && message.vendorId && message.vendorId !== vendorId) return false;
    if (String(message.senderRole).trim().toLowerCase() !== incomingRole) {
      return false;
    }

    const createdAt = Date.parse(message.createdAt);
    return Number.isFinite(createdAt) && createdAt > readAt;
  }).length;
}

export function filterCourierSellerOrderMessages(
  messages: OrderMessageView[] | undefined,
): OrderMessageView[] {
  return (messages ?? []).filter((message) => {
    const role = String(message.senderRole).trim().toLowerCase();
    return (role === "courier" || role === "seller") && message.channel !== "courier_buyer";
  });
}
